import { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ActionPillNode } from '@/tiptap/tiptap-extensions/common/action-pill';
import { Bold, Italic, List } from 'lucide-react';
import { Button } from '@deepguide-ai/ui/button';
import { cn } from '@/lib/utils';
import { deserializeFromMarkdown } from '@deepguide-ai/tiptap-shared';
import { renderToMarkdownReadable } from '@deepguide-ai/tiptap-shared/render';

interface SimpleInstructionsEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Render the markdown without toolbar, border, or editing. */
  readOnly?: boolean;
}

/**
 * Simple TipTap editor for workflow instructions
 * Supports: Bold, Italic, Ordered Lists
 */
export function SimpleInstructionsEditor({
  value,
  onChange,
  placeholder = 'Enter instructions...',
  className,
  readOnly = false,
}: SimpleInstructionsEditorProps) {
  // Track the last external value we synced to prevent loops
  const [lastSyncedValue, setLastSyncedValue] = useState<string | null>(null);

  // Parse markdown to TipTap JSON
  const parseMarkdownToContent = (markdown: string) => {
    if (!markdown) return '';
    const { content } = deserializeFromMarkdown(markdown);
    return content;
  };

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        // Disable features not needed for instructions
        // blockquote: false,
        // codeBlock: false,
        // horizontalRule: false,
        // strike: false,
        // Keep enabled: bold, italic, code (inline), bulletList, orderedList, listItem, paragraph, text, doc, hardBreak, heading
        heading: {
          levels: [2, 3, 4, 5], // Support ##, ###, ####, ##### for instructions structure
        },
      }),
      ActionPillNode,
    ],
    content: '',
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (event.key !== 'Tab') return false;
        if (!editor) return false;

        // 保持 Tab 焦点留在列表项内（即便光标在 hardBreak 后），避免浏览器默认把焦点移出编辑器
        if (editor.isActive('listItem')) {
          event.preventDefault();
          if (event.shiftKey) {
            editor.commands.liftListItem('listItem'); // Shift+Tab：反缩进
          } else {
            editor.commands.sinkListItem('listItem'); // Tab：缩进
          }
          return true;
        }

        return false;
      },
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none px-3 py-2',
          readOnly ? 'min-h-0' : 'min-h-[200px]',
          'prose-p:my-2 prose-ol:my-2 prose-li:my-1',
          className
        ),
      },
    },
    onUpdate: ({ editor }) => {
      const markdown = renderToMarkdownReadable(editor.getJSON())
      setLastSyncedValue(markdown); // Track what we sent to parent to prevent sync loops
      onChange(markdown);
    },
  });

  // Update editor content when value changes externally (e.g., from SOP parser)
  // Only sync if the value is different from what we last synced (prevents loops with onChange)
  useEffect(() => {
    if (editor && value !== lastSyncedValue) {
      // Only update if value is non-empty OR if we had content before (to handle clearing)
      if (value || lastSyncedValue) {
        const content = parseMarkdownToContent(value);
        editor.commands.setContent(content);
        setLastSyncedValue(value);
      }
    }
  }, [editor, value, lastSyncedValue]);

  if (!editor) {
    return null;
  }

  return (
    <div className={readOnly ? '' : 'overflow-hidden border rounded-md bg-card'}>
      {/* Toolbar */}
      {!readOnly && (
      <div className="flex items-center gap-1 p-2 border-b bg-card">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={cn(
            'h-8 w-8 p-0',
            editor.isActive('bold') && 'bg-muted'
          )}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={cn(
            'h-8 w-8 p-0',
            editor.isActive('italic') && 'bg-muted'
          )}
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={cn(
            'h-8 w-8 p-0',
            editor.isActive('orderedList') && 'bg-muted'
          )}
          title="Ordered List"
        >
          <List className="h-4 w-4" />
        </Button>
      </div>
      )}

      {/* Editor */}
      <EditorContent
        editor={editor}
        className='simple-instructions-editor'
        placeholder={placeholder}
      />
    </div>
  );
}
